import { TypedDocumentNode, gql } from '@apollo/client';
import { Suspense } from 'react';
import { FormattedMessage } from 'react-intl';
import { Outlet, generatePath, matchPath, useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { ContentWithStickyHeader } from '@sorare/core/src/atoms/layout/ContentWithStickyHeader';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import LoadingIndicator from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import {
  MLB_PLAY_PRO_FIXTURE_CHASE,
  MLB_PLAY_PRO_FIXTURE_CHASE_USERGROUP_USER,
  MLB_PLAY_PRO_FIXTURE_COMPETITIONS,
  MLB_PLAY_PRO_FIXTURE_GAMES,
  MLB_PLAY_PRO_FIXTURE_GAMES_ID,
  MLB_PLAY_PRO_FIXTURE_MY_LINEUPS,
} from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useEventsContext } from '@sorare/core/src/contexts/events';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import useQuery from '@sorare/core/src/hooks/graphql/useQuery';
import useFeatureFlags from '@sorare/core/src/hooks/useFeatureFlags';
import { Lifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { glossary, navLabels } from '@sorare/core/src/lib/glossary';
import { catchAll, useTypedParams } from '@sorare/core/src/lib/routing';
import { Navigate } from '@sorare/core/src/routing/Navigate';

import FixtureSelector from '@sorare/gameplay/src/components/fixture/FixtureSelector';
import { getFixtureQueryVariables } from '@sorare/gameplay/src/lib/so5';

import {
  BaseballPlayProFixtureLayoutQuery,
  BaseballPlayProFixtureLayoutQueryVariables,
} from './__generated__/layout.graphql';
import { LayoutParams } from './__generated__/routeParams';
import { chaseGameWeeks } from './chaseGameWeeks';

const MLB_GET_COMPETITIONS = gql`
  query BaseballPlayProFixtureLayoutQuery(
    $type: So5State
    $slug: String
    $withTraining: Boolean!
  ) {
    so5 {
      so5Fixture(type: $type, slug: $slug, sport: BASEBALL) {
        slug
        gameWeek
        mySo5LineupsCount(training: $withTraining)
        ...FixtureSelector_so5Fixture
      }
    }
  }
  ${FixtureSelector.fragments.so5Fixture}
` as TypedDocumentNode<
  BaseballPlayProFixtureLayoutQuery,
  BaseballPlayProFixtureLayoutQueryVariables
>;

const FixtureSelectorWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  padding: var(--double-unit);
`;

const NewWrapper = styled.div`
  top: 4px;
  right: 0;
  position: absolute;
  font: var(--t-label-s);
  color: var(--c-static-yellow-600);
`;

const PlayProLayout = () => {
  const { pathname } = useLocation();
  const { fixture: fixturePath } = useTypedParams<LayoutParams>();
  const { currentUser } = useCurrentUserContext();
  const { track } = useEventsContext();

  const isTabletAndAbove = useIsTabletAndAbove();
  const {
    flags: { useChaseLobbyUi = false },
  } = useFeatureFlags();

  const lifecycle = currentUser?.userSettings?.lifecycle as Lifecycle;

  const { data, error } = useQuery(MLB_GET_COMPETITIONS, {
    variables: {
      ...getFixtureQueryVariables(fixturePath),
      withTraining: !!lifecycle?.showTrainingLineups,
    },
  });

  const { so5 } = data || {};
  const { so5Fixture } = so5 || {};
  const fixtureNotFound = error?.graphQLErrors?.some(
    graphQlError => graphQlError.extensions?.code === 'NOT_FOUND'
  );

  if (fixtureNotFound) {
    return (
      <Navigate
        replace
        to={generatePath(MLB_PLAY_PRO_FIXTURE_MY_LINEUPS, {
          fixture: 'ongoing',
        })}
      />
    );
  }

  if (!so5Fixture) {
    return <LoadingIndicator />;
  }

  return (
    <>
      <PageHeader text={glossary.pro} />
      <ContentWithStickyHeader
        header={
          <FullWidthContainer.NoPadding>
            <FixtureSelectorWrapper>
              <FixtureSelector
                currentFixture={so5Fixture}
                disableLiveAndPast={false}
                navigateTo={fixture => {
                  const activeRoute = [
                    MLB_PLAY_PRO_FIXTURE_MY_LINEUPS,
                    MLB_PLAY_PRO_FIXTURE_GAMES,
                    MLB_PLAY_PRO_FIXTURE_COMPETITIONS,
                  ].find(route => matchPath(catchAll(route), pathname));

                  return generatePath(
                    activeRoute || MLB_PLAY_PRO_FIXTURE_MY_LINEUPS,
                    { fixture }
                  );
                }}
              />
            </FixtureSelectorWrapper>
            <TabBar fullWidth variant="flat">
              <Tab
                to={generatePath(MLB_PLAY_PRO_FIXTURE_MY_LINEUPS, {
                  fixture: so5Fixture.slug,
                })}
                matches={[MLB_PLAY_PRO_FIXTURE_MY_LINEUPS]}
              >
                <FormattedMessage
                  {...(isTabletAndAbove
                    ? navLabels.myLineupsWithNumber
                    : navLabels.lineupsWithNumber)}
                  values={{ nb: so5Fixture?.mySo5LineupsCount || 0 }}
                />
              </Tab>
              <Tab
                to={generatePath(MLB_PLAY_PRO_FIXTURE_COMPETITIONS, {
                  fixture: so5Fixture.slug,
                })}
                matches={[MLB_PLAY_PRO_FIXTURE_COMPETITIONS]}
              >
                <FormattedMessage {...navLabels.competitions} />
              </Tab>
              {useChaseLobbyUi && chaseGameWeeks[so5Fixture.gameWeek] && (
                <Tab
                  to={generatePath(MLB_PLAY_PRO_FIXTURE_CHASE, {
                    fixture: so5Fixture.slug,
                  })}
                  matches={[
                    MLB_PLAY_PRO_FIXTURE_CHASE,
                    MLB_PLAY_PRO_FIXTURE_CHASE_USERGROUP_USER,
                  ]}
                  onClick={() =>
                    track(
                      `Click ${chaseGameWeeks[so5Fixture.gameWeek]} Lobby Tab`,
                      {
                        sport: Sport.BASEBALL,
                      }
                    )
                  }
                >
                  {isTabletAndAbove ? (
                    chaseGameWeeks[so5Fixture.gameWeek]
                  ) : (
                    <FormattedMessage {...navLabels.chase} />
                  )}
                  <NewWrapper>
                    <FormattedMessage
                      id="Baseball.playPage.tabs.new"
                      defaultMessage="New!"
                    />
                  </NewWrapper>
                </Tab>
              )}
              <Tab
                to={generatePath(MLB_PLAY_PRO_FIXTURE_GAMES, {
                  fixture: so5Fixture.slug,
                })}
                matches={[
                  MLB_PLAY_PRO_FIXTURE_GAMES,
                  MLB_PLAY_PRO_FIXTURE_GAMES_ID,
                ]}
              >
                <FormattedMessage {...glossary.games} />
              </Tab>
            </TabBar>
          </FullWidthContainer.NoPadding>
        }
      >
        <Suspense fallback={<LoadingIndicator fullHeight />}>
          <Outlet />
        </Suspense>
      </ContentWithStickyHeader>
    </>
  );
};

export default PlayProLayout;
